import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { ConnectionIcon } from 'app/common/presentation'
import {
  CardConnectionsIcon,
  CardObjectivesIcon,
  CardSignalsIcon,
  ModelIcon,
  NutIcon,
} from 'app/common/presentation/icons'
import { useConnections } from 'app/connections/application'
import { mapToUrl } from 'app/connections/presentation/connectionUrlMapper'
import { maskValue } from 'app/connections/presentation/maskValue'
import { useObjectives } from 'app/objectives/application'
import { useSignalModels, useSignals } from 'app/signals/application'

import { Card, CodeTag, PageBanner, Table } from 'components'

import RelatedItem from './RelatedItem'
import styles from './SignalDetails.module.scss'
import TreatmentUsage from './TreatmentUsage'
import { getRelatedEntries } from './getRelatedEntries'

const date = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))

const SignalDetails = () => {
  const { id } = useParams()
  const { signals } = useSignals()
  const { connections } = useConnections()
  const { getObjective } = useObjectives()
  const { t } = useTranslation('signals')
  const signal = signals.find((entry) => entry.id === id)
  const selectedSignals = useMemo(() => (signal ? [signal] : []), [signal])
  const models = useSignalModels(selectedSignals)
  if (!signal) return null

  const model = signal.model ? models.get(signal.model) : undefined
  const objective = getObjective(signal.objective)
  const related = getRelatedEntries(signal.relatesTo)
  const platform = signal.connection.platform.name
  const connection = connections.find((entry) => entry.id === signal.connection.platform.id)
  const connectionState =
    connection?.status === 'active' ? 'connected' : (connection?.status ?? 'disconnected')
  const accountDescription = signal.connection.account
    ? `${signal.connection.account.name} ${maskValue(signal.connection.account.id)}`
    : undefined
  const rows = [
    ...(signal.config.lookbackDays === undefined
      ? []
      : [{ name: 'lookbackDays', value: signal.config.lookbackDays }]),
    ...(signal.config.size == null ? [] : [{ name: 'size', value: signal.config.size }]),
  ]

  return (
    <div>
      <PageBanner
        leading={
          <span className={styles.platformIcon}>
            <ConnectionIcon connection={{ name: platform }} />
          </span>
        }
        title={signal.name}
        titleSuffix={<CodeTag>{t(`overview.purpose.${signal.config.purpose}`)}</CodeTag>}
      >
        <span className={styles.created}>
          {t('details.created')} <strong>{date(signal.createdAt)}</strong>
        </span>
      </PageBanner>
      <div className={styles.columns}>
        <div className={styles.column}>
          {signal.model && (
            <Card
              icon={<ModelIcon />}
              title={t('details.model')}
              titleSuffix={
                <CodeTag>
                  <span className={styles.modelPath} title={model?.path}>
                    {model?.path ?? signal.model}
                  </span>
                </CodeTag>
              }
            >
              {model && (
                <dl className={styles.metrics}>
                  <div>
                    <dt>{t('details.created')}</dt>
                    <dd>{date(model.created)}</dd>
                  </div>
                  <div>
                    <dt>{t('details.f1Score')}</dt>
                    <dd>{model.f1Score ?? '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('details.scope')}</dt>
                    <dd>{model.scope ? <CodeTag color="grey">{model.scope}</CodeTag> : '-'}</dd>
                  </div>
                </dl>
              )}
              {signal.config.treatments && <TreatmentUsage key={signal.id} signal={signal} />}
            </Card>
          )}
        </div>
        <div className={styles.column}>
          {rows.length > 0 && (
            <Card title={t('details.configuration')} icon={<NutIcon />}>
              <Table.Table
                className={styles.configuration}
                columns={[]}
                data={rows}
                renderRow={(row) => (
                  <Table.Row key={row.name}>
                    <Table.Cell>{row.name}</Table.Cell>
                    <Table.Cell>{row.value}</Table.Cell>
                  </Table.Row>
                )}
              />
            </Card>
          )}
          {objective && (
            <Card title={t('details.objectives')} icon={<CardObjectivesIcon />}>
              <div className={styles.relatedList}>
                <RelatedItem
                  name={objective.name}
                  icon={
                    <ConnectionIcon
                      connection={{ name: platform }}
                      style={{ width: 20, height: 20 }}
                    />
                  }
                  description={`${objective.aggregation.type} · ${objective.direction} · ${objective.per}`}
                  to={`/objectives/${objective.id}`}
                />
              </div>
            </Card>
          )}
          <Card title={t('details.connections')} icon={<CardConnectionsIcon />}>
            <div className={styles.relatedList}>
              <RelatedItem
                name={t(`platform.${platform}`)}
                icon={
                  <ConnectionIcon
                    connection={{ name: platform }}
                    style={{ width: 20, height: 20 }}
                  />
                }
                description={[accountDescription, t(`details.connectionState.${connectionState}`)]
                  .filter(Boolean)
                  .join(' · ')}
                to={connection ? `/connections/${mapToUrl(platform)}/manage` : undefined}
              />
            </div>
          </Card>
          {related.length > 0 && (
            <Card title={t('details.signals')} icon={<CardSignalsIcon />}>
              <div className={styles.relatedList}>
                {related.map((entry) => (
                  <RelatedItem
                    key={`${entry.platform}/${entry.id}`}
                    name={entry.name}
                    icon={
                      <ConnectionIcon
                        connection={{ name: entry.platform }}
                        style={{ width: 20, height: 20 }}
                      />
                    }
                  />
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default SignalDetails
